import type { AssetNoticeCount, PlanningSplit } from '@/modules/pipelines/data/metrics';
import styles from '@/modules/pipelines/components/deep-dive/asset-metrics/asset-notice-ranking.module.scss';

interface StatusSlice {
  key: keyof PlanningSplit;
  label: string;
  colorVar: string;
}

const SLICES: StatusSlice[] = [
  { key: 'unplanned', label: 'Unplanned', colorVar: 'var(--status-critical, #d03b3b)' },
  { key: 'planned', label: 'Planned', colorVar: 'var(--status-planned, #2a78d6)' },
  { key: 'unknown', label: 'Unknown', colorVar: 'var(--status-neutral-soft, #d6d6d1)' },
];

function breakdownText(planning: PlanningSplit): string {
  return SLICES.filter(slice => planning[slice.key] > 0)
    .map(slice => `${slice.label}: ${planning[slice.key].toLocaleString()}`)
    .join('\n');
}

export default function AssetNoticeRanking({
  assets,
  windowLabel,
  limit = 10,
}: {
  assets: AssetNoticeCount[];
  windowLabel: string;
  limit?: number;
}) {
  const top = assets.slice(0, limit);

  return (
    <div className={styles.wrapper}>
      <div className={styles.header}>
        <span className={styles.title}>Assets most often on a notice</span>
        <span className={styles.subtitle}>
          {top.length ? `top ${top.length} · ${windowLabel}` : windowLabel}
        </span>
      </div>

      {!top.length && <div className={styles.empty}>No assets found.</div>}

      {top.length > 0 && (
        <>
          <ul className={styles.legend}>
            {SLICES.map(slice => (
              <li key={slice.key} className={styles.legendItem}>
                <span
                  className={styles.legendDot}
                  style={{ background: slice.colorVar }}
                  aria-hidden={'true'}
                />
                {slice.label}
              </li>
            ))}
          </ul>

          <ul className={styles.rows}>
            {top.map(asset => {
              const label = asset.pipelineName
                ? `${asset.name} (${asset.pipelineName})`
                : asset.name;

              return (
                <li key={asset.id} className={styles.row} tabIndex={0}>
                  <div>
                    <div className={styles.label}>
                      {asset.name}
                      {asset.pipelineName && (
                        <span className={styles.pipeline}> · {asset.pipelineName}</span>
                      )}
                    </div>

                    <div
                      className={styles.track}
                      title={`${label}\n${asset.notices.toLocaleString()} notices\n${breakdownText(
                        asset.planning,
                      )}\n${windowLabel}`}
                      role={'img'}
                      aria-label={`${label}: ${asset.notices.toLocaleString()} notices — ${SLICES.map(
                        slice => `${slice.label} ${asset.planning[slice.key]}`,
                      ).join(', ')}`}
                    >
                      {SLICES.filter(slice => asset.planning[slice.key] > 0).map(slice => (
                        <div
                          key={slice.key}
                          className={styles.segment}
                          style={{
                            flexGrow: asset.planning[slice.key],
                            background: slice.colorVar,
                          }}
                        />
                      ))}
                    </div>
                  </div>

                  <div className={styles.value}>{asset.notices.toLocaleString()}</div>
                </li>
              );
            })}
          </ul>
        </>
      )}
    </div>
  );
}
